import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from './store/authStore'
import { useState, useEffect } from 'react'
import { RETURN_AFTER_SIGN_IN_KEY, SESSION_EXPIRED_EVENT } from './services/api'
import { useCookieConsentVisibility } from './hooks/useCookieConsentVisibility'
import Layout from './components/Layout'
import Login from './pages/Login'
import SignedOutRedirect from './components/SignedOutRedirect'
import SignUp from './pages/SignUp'
import Activate from './pages/Activate'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import Account from './pages/Account'
import AppStore from './pages/AppStore'
import AppDetail from './pages/AppDetail'
import PublishApp from './pages/PublishApp'
import MyApps from './pages/MyApps'
import AdminDashboard from './pages/AdminDashboard'
import Settings from './pages/Settings'
import IntegrationsSettings from './pages/IntegrationsSettings'
import SubscriptionSettings from './pages/SubscriptionSettings'
import SecuritySettings from './pages/SecuritySettings'
import DeploymentExperience from './pages/DeploymentExperience'
import Templates from './pages/Templates'
import BrandingSettings from './pages/BrandingSettings'
import BrandingDeploymentDetail from './pages/BrandingDeploymentDetail'
import IntuneIntegration from './pages/IntuneIntegration'
import UsersSettings from './pages/UsersSettings'
import TenantsSettings from './pages/TenantsSettings'
import JobStatus from './pages/JobStatus'
import Deployments from './pages/Deployments'
import DeploymentDetail from './pages/DeploymentDetail'
import Events from './pages/Events'
import Discovery from './pages/Discovery'
import SetPassword from './pages/SetPassword'
import AcceptInvitation from './pages/AcceptInvitation'
import Devices from './pages/Devices'
import DeviceDetail from './pages/DeviceDetail'
import Bundles from './pages/Bundles'
import BundleDetail from './pages/BundleDetail'
import SSOCallback from './pages/SSOCallback'
import SSOConfiguration from './pages/SSOConfiguration'
import MSPIntuneOnboarding from './pages/MSPIntuneOnboarding'

function SessionExpiredModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-theme-card rounded-lg shadow-xl max-w-md w-full mx-4 p-6 border border-theme-border-primary">
        <div className="flex items-start mb-4">
          <div className="flex-shrink-0 w-10 h-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center">
            <svg className="h-6 w-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div className="ml-4">
            <h3 className="text-lg font-semibold text-theme-text-primary">Session Expired</h3>
            <p className="mt-1 text-sm text-theme-text-secondary">
              Your session has expired. Please sign in again to continue.
            </p>
          </div>
        </div>
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
          >
            Sign In
          </button>
        </div>
      </div>
    </div>
  )
}

function App() {
  const { isAuthenticated, isTenantAdmin, hasPermission } = useAuthStore()
  const [showSessionExpired, setShowSessionExpired] = useState(false)

  // Cookie consent belongs to the public pages; inside the product it stays hidden.
  useCookieConsentVisibility(!isAuthenticated)

  useEffect(() => {
    const handleSessionExpired = () => {
      setShowSessionExpired(true)
    }

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired)
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired)
    }
  }, [])

  const ProtectedRoute = ({ children, requireAdmin = false, requirePermission }: {
    children: React.ReactNode
    requireAdmin?: boolean
    requirePermission?: string
  }) => {
    if (!isAuthenticated) {
      return <Navigate to="/login" replace />
    }

    if (requireAdmin && !isTenantAdmin()) {
      return <Navigate to="/" replace />
    }

    if (requirePermission && !hasPermission(requirePermission)) {
      return <Navigate to="/" replace />
    }

    return children
  }

  // After a sign-in that follows an expired session, back to the page the session ended on.
  const navigate = useNavigate()
  const location = useLocation()
  useEffect(() => {
    if (!isAuthenticated || location.pathname === '/login') return
    let target: string | null = null
    try {
      target = window.sessionStorage.getItem(RETURN_AFTER_SIGN_IN_KEY)
      window.sessionStorage.removeItem(RETURN_AFTER_SIGN_IN_KEY)
    } catch {
      return
    }
    if (target && target !== location.pathname && target.startsWith('/') && !target.startsWith('/login')) {
      navigate(target, { replace: true })
    }
    // Runs again once the sign-in page has handed over to the app; the key is read only once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, location.pathname])

  const handleSessionExpiredClose = () => {
    setShowSessionExpired(false)
    // Navigate to login is handled by the auth store logout
  }

  return (
    <>
      {showSessionExpired && <SessionExpiredModal onClose={handleSessionExpiredClose} />}
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/activate" element={<Activate />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/accept-invitation" element={<AcceptInvitation />} />
        <Route path="/sso/callback" element={<SSOCallback />} />

      <Route
        path="/"
        element={
          isAuthenticated ? <Layout /> : <SignedOutRedirect />
        }
      >
        <Route index element={<AppStore />} />
        <Route path="apps/:appId" element={<AppDetail />} />
        <Route path="my-apps" element={<MyApps />} />

        {/* Your own account. No permission gate: every signed-in user has one, and the endpoints
            behind it act on the caller rather than on a user chosen by id. */}
        <Route
          path="account"
          element={
            <ProtectedRoute>
              <Account />
            </ProtectedRoute>
          }
        />

        {/* Operational pages -- IT User + Admin (permission-gated) */}
        <Route
          path="publish"
          element={
            <ProtectedRoute requirePermission="manage_apps">
              <PublishApp />
            </ProtectedRoute>
          }
        />

        <Route
          path="deployments"
          element={
            <ProtectedRoute requirePermission="deploy_apps">
              <Deployments />
            </ProtectedRoute>
          }
        />

        {/* One deployment. A real URL rather than a modal, so a rollout can be linked to. */}
        <Route
          path="deployments/:deploymentId"
          element={
            <ProtectedRoute requirePermission="deploy_apps">
              <DeploymentDetail />
            </ProtectedRoute>
          }
        />

        <Route
          path="events"
          element={
            <ProtectedRoute requirePermission="view_jobs">
              <Events />
            </ProtectedRoute>
          }
        />

        <Route
          path="discovery"
          element={
            <ProtectedRoute requirePermission="manage_apps">
              <Discovery />
            </ProtectedRoute>
          }
        />

        <Route
          path="jobs"
          element={
            <ProtectedRoute requirePermission="view_jobs">
              <JobStatus />
            </ProtectedRoute>
          }
        />

        <Route
          path="bundles"
          element={
            <ProtectedRoute requirePermission="deploy_apps">
              <Bundles />
            </ProtectedRoute>
          }
        />
        <Route
          path="bundles/:bundleId"
          element={
            <ProtectedRoute requirePermission="deploy_apps">
              <BundleDetail />
            </ProtectedRoute>
          }
        />

        {/* Admin-only pages */}
        <Route
          path="admin"
          element={
            <ProtectedRoute requireAdmin={true}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="teams/users"
          element={
            <ProtectedRoute requireAdmin={true}>
              <UsersSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="teams/tenants"
          element={
            <ProtectedRoute requireAdmin={true}>
              <TenantsSettings />
            </ProtectedRoute>
          }
        />

        <Route
          path="settings"
          element={
            <ProtectedRoute requireAdmin={true}>
              <Settings />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/settings/integrations" replace />} />
          <Route path="integrations" element={<IntegrationsSettings />} />
          {/* 016-template-model: Templates has replaced this in the navigation. Still routed,
              because it is the only editor for branding rows a tenant may already have and
              publish still reads them — an unlinked page beats stranded data. */}
          <Route path="deployment-experience" element={<DeploymentExperience />} />
          <Route path="templates" element={<Templates />} />
          {/* Branding is device-wide, not a template: its own settings page rather than a
              third tab, so nobody looks for it in the deploy wizard's template picker. */}
          <Route path="branding" element={<BrandingSettings />} />
          {/* One deployment. Its own URL for the same reason an application deployment has one:
              it carries per-device state nobody can link a colleague to from inside a table. */}
          <Route path="branding/deployments/:deploymentId" element={<BrandingDeploymentDetail />} />
          <Route path="subscription" element={<SubscriptionSettings />} />
          {/* How members sign in: password rules, required MFA, session lifetime. */}
          <Route path="security" element={<SecuritySettings />} />
          <Route path="integrations/intune" element={<IntuneIntegration />} />
          <Route path="integrations/msp-intune" element={<MSPIntuneOnboarding />} />
          <Route path="integrations/sso" element={<SSOConfiguration />} />
          <Route path="jobs" element={<JobStatus />} />
        </Route>
      </Route>
      </Routes>
    </>
  )
}

export default App
